import { useCallback, useEffect, useState } from 'react';

import { useSelector, useYearQuarterNavigation } from 'hooks';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  Scenario,
  WorkDemand,
  WorkDemandRow,
  WorkDemandRowWithoutId
} from 'components/types';

import { deleteWorkDemand, getWorkDemand } from 'RestAPIs/care-demand';

import { showSnackbar } from 'components/shared';

import AddEditWorkDemandDialog from './AddEditWorkDemandDialog';
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import { AggregateOn } from 'components/enums';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

type Props = {
  scenario: Scenario;
  updateTotals: () => void;
};

const WorkDemandGrid = ({ scenario, updateTotals }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [workDemand, setWorkDemand] = useState<WorkDemand>();

  const [shouldShowAddEditDialog, setShouldShowAddEditDialog] =
    useState<boolean>(false);

  const [addEditDialogData, setAddEditDialogData] = useState<
    WorkDemandRow | WorkDemandRowWithoutId | null
  >(null);

  const { hasCapacityPortalEditAccess } = useSelector((state) => state.grants);
  const { year, aggregate } = useYearQuarterNavigation();
  const { t } = useTranslation('common');

  const columns = [
    {
      field: 'costCenterId',
      headerName: t('costCenter'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'functionId',
      headerName: t('function'),
      flex: 1,
      minWidth: 150
    },
    { field: 'quantity', headerName: t('quantity') },
    { field: 'directClientHours', headerName: t('directHours') },
    { field: 'indirectClientHours', headerName: t('indirectHours') },
    { field: 'organisationHours', headerName: t('organizationHours') },
    { field: 'unit', headerName: t('unit') },
    {
      field: 'validFrom',
      headerName: t('validFrom'),
      valueFormatter: dateFormatter
    },
    {
      field: 'validTo',
      headerName: t('validTo'),
      valueFormatter: dateFormatter
    },
    { field: 'hoursPerYear', headerName: t('hoursPerYear') }
  ];

  const rows = workDemand
    ? workDemand.map((w) => ({
        ...w,
        costCenterId:
          scenario.costCenters.find(({ id }) => id === w.costCenterId)
            ?.nameShort || '',
        functionId: w?.function?.description ?? '',
        unit: t(`timeUnits.${w.unit}`),
        validFrom: w.timeframe.from,
        validTo: w.timeframe.to
      }))
    : [];

  const loadWorkDemand = useCallback(() => {
    if (scenario?.scenarioId) {
      setWorkDemand([]);
      setIsLoading(true);

      getWorkDemand({
        scenarioId: scenario.scenarioId,
        year,
        aggregateOn: aggregate || AggregateOn.MONTH
      })
        .then(({ data }) => {
          setWorkDemand(data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [scenario, year, aggregate]);

  const handleCloseAddEdit = () => {
    setShouldShowAddEditDialog(false);
    setAddEditDialogData(null);
  };

  useEffect(() => {
    loadWorkDemand();
  }, [scenario, year]);

  const onClickAddRow = () => {
    setShouldShowAddEditDialog(true);
  };

  const onClickCopyRow = (id: number) => {
    setAddEditDialogData(
      omit(workDemand?.find(({ id: cId }) => cId === id), [
        'id'
      ]) as WorkDemandRowWithoutId
    );
    setShouldShowAddEditDialog(true);
  };

  const onClickDeleteRow = (selectedRows: number[]) => {
    deleteWorkDemand(selectedRows)
      .then(() => {
        loadWorkDemand();
        showSnackbar({
          message: t('changesSaved'),
          severity: 'success'
        });
      })
      .catch(() => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      })
      .finally(() => {
        updateTotals();
      });
  };

  const onClickEditRow = (id: number) => {
    setAddEditDialogData(
      workDemand?.find(({ id: cId }) => cId === id) as WorkDemandRow
    );
    setShouldShowAddEditDialog(true);
  };

  const onSaveWorkDemand = () => {
    handleCloseAddEdit();
    loadWorkDemand();
    updateTotals();
  };

  return (
    <TableCRUDLayout
      columns={columns}
      rows={rows}
      shouldRenderToolBar={false}
      renderFormContainer={false}
      editable={false}
      disableActions={!hasCapacityPortalEditAccess || scenario?.readOnly}
      onAddClick={onClickAddRow}
      onCopyClick={(id) => onClickCopyRow(id as number)}
      onEditClick={(id) => onClickEditRow(id as number)}
      onDeleteClick={(idsList) => onClickDeleteRow(idsList as number[])}
      loading={isLoading}
      checkboxSelection
      confirmationTitle={t('workDemand.deleteConfirmationDialogTitle')}
      confirmationBody={t('workDemand.deleteConfirmationDialogBody')}
      addRowDialog={
        <AddEditWorkDemandDialog
          isOpen={shouldShowAddEditDialog}
          workDemand={addEditDialogData}
          handleClose={handleCloseAddEdit}
          onSave={onSaveWorkDemand}
          scenario={scenario}
        />
      }
    />
  );
};

export default WorkDemandGrid;
